import { useLayoutEffect, useRef, useState } from "react";

import type { Reference } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { resolveReferenceDisplays } from "@/billing/services/reference-displays";
import type { ReferenceDisplayCache } from "@/billing/types/reference-displays";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

interface UseResolvedReferenceDisplaysOptions {
  /** When false, nothing is read and any resolution still in flight is abandoned. Defaults to true. */
  isEnabled?: boolean;
  /** A changed value starts a new resolution for the same references, retrying transient failures. */
  refreshKey?: string;
}

interface UseResolvedReferenceDisplaysResult {
  /** Names resolved so far, keyed by reference string. */
  displays: ReferenceDisplayCache;
  /** True while the newest resolution is still reading. */
  isResolving: boolean;
}

/**
 * Resolves display names for a list of references and keeps them for the rest of the session.
 *
 * References read successfully (or proved unreadable with a 403/404) cost no further request, so
 * paging back or repeating a search re-renders from the cache. A reference whose read failed
 * transiently is retried by the next resolution that includes it. Each publish is a new Map, so
 * consumers re-render on the value they read.
 *
 * Only the newest resolution reports a whole-resolution failure or drives `isResolving`, so a slow
 * read for a page or query the user has already left cannot overwrite what they are looking at.
 * Per-reference read failures are reported by `resolveReferenceDisplays` as they happen, whichever
 * resolution they belong to; each carries the reference string only. A superseded resolution still
 * adds its names to the cache, and publishes them only when it finishes with a name the current
 * references use that the cache lacked.
 *
 * Pass a memoized `references` array: a new array identity starts a new resolution.
 *
 * @param references The references to resolve.
 * @param options `isEnabled` turns resolution on or off; `refreshKey` restarts it when changed.
 */
export function useResolvedReferenceDisplays(
  references: Reference[],
  { isEnabled = true, refreshKey }: UseResolvedReferenceDisplaysOptions = {},
): UseResolvedReferenceDisplaysResult {
  const medplum = useMedplum();
  const [displays, setDisplays] = useState<ReferenceDisplayCache>(() => new Map());
  const [isResolving, setIsResolving] = useState(false);
  const resolvedSoFarRef = useRef<ReferenceDisplayCache>(new Map());
  const latestResolutionRef = useRef(0);
  const currentReferenceStringsRef = useRef<Set<string>>(new Set());

  // A layout effect, so `isResolving` turns true before the browser paints the render that needs a
  // resolution. A passive effect would let that render paint once with `isResolving: false` and the
  // old names, which a caller shows as an empty "no results" state.
  useLayoutEffect(() => {
    if (!isEnabled || references.length === 0) {
      // Abandon any in-flight resolution: its result is no longer what the caller is showing.
      latestResolutionRef.current += 1;
      currentReferenceStringsRef.current = new Set();
      setIsResolving(false);
      return;
    }

    const resolutionId = ++latestResolutionRef.current;
    currentReferenceStringsRef.current = new Set(
      references.flatMap((reference) => (reference.reference ? [reference.reference] : [])),
    );
    // Names a superseded resolution cached but never published go out now, so rows whose name is
    // already known do not wait on the reads this run still has to make. The cache only grows and
    // every publish is a copy of it, so a matching size means nothing new to publish.
    setDisplays((published) =>
      published.size === resolvedSoFarRef.current.size
        ? published
        : new Map(resolvedSoFarRef.current),
    );
    // With every reference already resolved there is nothing to read, and a run would only flash the
    // caller's loading state.
    if (!hasUnresolvedReference(references, resolvedSoFarRef.current)) {
      setIsResolving(false);
      return;
    }
    setIsResolving(true);

    (async () => {
      try {
        // Seeded with the names resolved so far. Resolving into a copy leaves the Map on screen
        // untouched.
        const resolved = await resolveReferenceDisplays(
          medplum,
          references,
          new Map(resolvedSoFarRef.current),
        );
        // Merged, not replaced, even for a superseded resolution: two resolutions can run at once,
        // and whichever finishes last must not erase the other's names.
        const previouslyResolved = resolvedSoFarRef.current;
        resolvedSoFarRef.current = new Map([...previouslyResolved, ...resolved]);
        // The newest resolution always publishes. A superseded one publishes only when it added a
        // name the current references use, since the newest may have left that one out after a
        // transient failure. The published Map is a copy of the merged cache, so it never drops a
        // name already shown.
        const isSuperseded = resolutionId !== latestResolutionRef.current;
        if (isSuperseded) {
          const hasNewCurrentName = [...resolved.keys()].some(
            (referenceString) =>
              !previouslyResolved.has(referenceString) &&
              currentReferenceStringsRef.current.has(referenceString),
          );
          if (!hasNewCurrentName) return;
        }
        setDisplays(new Map(resolvedSoFarRef.current));
      } catch (error) {
        if (resolutionId !== latestResolutionRef.current) return;
        captureExceptionWithoutPhi("Failed to resolve reference displays", error);
      } finally {
        if (resolutionId === latestResolutionRef.current) setIsResolving(false);
      }
    })();
  }, [medplum, references, isEnabled, refreshKey]);

  return { displays, isResolving };
}

/** True when some reference has no resolved name in `resolved` yet. */
function hasUnresolvedReference(references: Reference[], resolved: ReferenceDisplayCache): boolean {
  return references.some(
    (reference) => !!reference.reference && !resolved.has(reference.reference),
  );
}
